import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Users, MessageCircle, Calendar, Activity, Trophy, TrendingUp, Code2, Star, ArrowUpRight, ArrowDownRight, Minus, Hash, Clock, AlertCircle, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import BackToTop from './BackToTop';
import { generateForumDescription } from '../utils/forumDescriptions';
import { createCategorySlug, createSoftwareSlug } from '../utils/urlHelpers';
import { formatLastUpdated, lastUpdatedSource } from '../utils/formatLastUpdated';
import { formatStatDelta, statDelta } from '../utils/forumStatChange';
import { getForumGrowthPercent } from '../utils/forumGrowth';
import { applyPageSeo, absoluteUrl } from '../utils/seo';
import { injectJsonLd, resetMetaTags, removeAllJsonLd } from '../utils/meta';
import ForumRiskBadge from './ForumRiskBadge';
import { isSponsorActive } from '../utils/sponsor';
import type { ForumBase } from '../types/forum';
import ForumCardWrapper from './ForumCardWrapper';

interface Forum {
  id: string;
  forum_id: string;
  name: string;
  url: string;
  category: string;
  topics_count: number;
  posts_count: number;
  members_count: number;
  founded_year: number;
  description: string;
  logo_url?: string;
  is_active: boolean;
  is_sponsored: boolean;
  sponsored_until: string | null;
  software?: string | null;
  previous_month_topics_count: number;
  previous_month_posts_count: number;
  previous_month_members_count: number;
  updated_at: string;
}

interface Category {
  id: string;
  name: string;
  description: string;
  previous_month_forums_count: number;
}

interface SiteSettings {
  site_name: string;
  description: string;
}

interface Props {
  darkMode: boolean;
}

type SortKey = keyof Forum | 'score' | 'growth';

const ForumStats: React.FC<Props> = ({ darkMode }) => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [sortBy, setSortBy] = useState<SortKey>((searchParams.get('sort') as SortKey) || 'score');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('cat') || '');
  const [selectedSoftware, setSelectedSoftware] = useState(searchParams.get('sw') || '');
  const [minMembers, setMinMembers] = useState(Number(searchParams.get('min') || 0));
  const [forums, setForums] = useState<Forum[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<SiteSettings>({
    site_name: 'Türkiye\'nin En Büyük Forum Siteleri',
    description: 'Türkiye\'nin en popüler forum sitelerinin güncel istatistikleri ve sıralamaları'
  });

  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      setError(null);
      try {
        await Promise.all([
          fetchSettings(),
          fetchForums(),
          fetchCategories()
        ]);
      } catch (error) {
        console.error('Error initializing data:', error);
        setError('Veriler yüklenirken bir hata oluştu. Lütfen internet bağlantınızı kontrol edin.');
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  useEffect(() => {
    applyPageSeo({
      title: settings.site_name,
      description: settings.description,
      keywords:
        'türk forumları, forum istatistikleri, forum siteleri, forum karşılaştırma, türkiye forum listesi, forum sıralaması',
      canonical: absoluteUrl('/'),
    });
    injectJsonLd(
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'kitapcik.net',
        alternateName: settings.site_name,
        url: absoluteUrl('/'),
        description: settings.description,
        inLanguage: 'tr-TR',
        potentialAction: {
          '@type': 'SearchAction',
          target: `${absoluteUrl('/')}?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
      'json-ld-website'
    );
    return () => {
      resetMetaTags();
      removeAllJsonLd();
    };
  }, [settings.site_name, settings.description]);

  const fetchSettings = async () => {
    try {
      // Add timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const { data, error: supabaseError } = await supabase
        .from('site_settings')
        .select('site_name, description')
        .limit(1)
        .single()
        .abortSignal(controller.signal);

      clearTimeout(timeoutId);

      if (supabaseError) {
        console.warn('Settings fetch error:', supabaseError.message);
        // Don't throw error for settings, use defaults
        return;
      }

      if (data) {
        setSettings({
          site_name: data.site_name,
          description: data.description
        });
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Bağlantı zaman aşımına uğradı');
      }
      console.warn('Error fetching settings:', error);
      // Use default settings on error
    }
  };

  const fetchForums = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const { data, error: supabaseError } = await supabase
        .from('forums')
        .select('*')
        .eq('is_active', true)
        .abortSignal(controller.signal);

      clearTimeout(timeoutId);
      
      if (supabaseError) throw supabaseError;
      setForums(data || []);
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Forum verileri yüklenirken zaman aşımına uğradı');
      }
      console.error('Error fetching forums:', error);
      setForums([]);
      throw error;
    }
  };

  const fetchCategories = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const { data, error: supabaseError } = await supabase
        .from('categories')
        .select('*')
        .order('name')
        .abortSignal(controller.signal);

      clearTimeout(timeoutId);
      
      if (supabaseError) {
        console.warn('Categories fetch error:', supabaseError.message);
        // Don't throw error for categories, they're optional
        return;
      }

      setCategories(data || []);
    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn('Categories fetch timeout');
      } else {
        console.warn('Error fetching categories:', error);
      }
      setCategories([]);
    }
  };

  const calculateScore = (forum: Forum): number => {
    const topicsScore = ((forum.topics_count ?? 0) * 5);
    const postsScore = ((forum.posts_count ?? 0) * 2);
    const membersScore = ((forum.members_count ?? 0) * 10);
    return Math.round((topicsScore + postsScore + membersScore) / (forum.founded_year || 1));
  };

  const calculatePreviousScore = (forum: Forum): number => {
    const topicsScore = ((forum.previous_month_topics_count ?? 0) * 5);
    const postsScore = ((forum.previous_month_posts_count ?? 0) * 2);
    const membersScore = ((forum.previous_month_members_count ?? 0) * 10);
    return Math.round((topicsScore + postsScore + membersScore) / (forum.founded_year || 1));
  };

  const getScoreChange = (forum: Forum): number | null => {
    const currentScore = calculateScore(forum);
    const previousScore = calculatePreviousScore(forum);
    if (previousScore <= 0) return null;
    const change = currentScore - previousScore;
    return change === 0 ? null : change;
  };

  const getChangeIndicator = (current: number, previous: number) => {
    if (current === previous) return <Minus size={16} className="text-gray-400" />;
    return current > previous ? (
      <ArrowUpRight size={16} className="text-green-500" />
    ) : (
      <ArrowDownRight size={16} className="text-red-500" />
    );
  };

  const getChangePercentage = (current: number, previous: number): string => {
    if (previous === 0) return '0%';
    const change = ((current - previous) / previous) * 100;
    return `${change > 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  const getTrendIndicator = (forum: Forum) => {
    const score = calculateScore(forum);
    if (score >= 100000) return 'Çok Aktif';
    if (score >= 40000) return 'Aktif';
    return 'Normal';
  };

  const getRankingByField = (forum: Forum, field: keyof Forum): number => {
    return forums
      .sort((a, b) => ((b[field] as number) ?? 0) - ((a[field] as number) ?? 0))
      .findIndex(f => f.id === forum.id) + 1;
  };

  const globalScoreRanks = useMemo(() => {
    const sorted = [...forums].sort((a, b) => calculateScore(b) - calculateScore(a));
    const ranks = new Map<string, number>();
    sorted.forEach((forum, index) => ranks.set(forum.id, index + 1));
    return ranks;
  }, [forums]);

  const globalPreviousScoreRanks = useMemo(() => {
    const sorted = [...forums].sort((a, b) => calculatePreviousScore(b) - calculatePreviousScore(a));
    const ranks = new Map<string, number>();
    sorted.forEach((forum, index) => ranks.set(forum.id, index + 1));
    return ranks;
  }, [forums]);

  const getRandomPosition = (min: number, max: number): number => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  const softwareList = useMemo(() => {
    const s = new Set<string>();
    forums.forEach((f) => {
      if (f.software?.trim()) s.add(f.software.trim());
    });
    return [...s].sort();
  }, [forums]);

  useEffect(() => {
    const p = new URLSearchParams();
    if (searchTerm) p.set('q', searchTerm);
    if (selectedCategory) p.set('cat', selectedCategory);
    if (selectedSoftware) p.set('sw', selectedSoftware);
    if (minMembers > 0) p.set('min', String(minMembers));
    if (sortBy !== 'score') p.set('sort', sortBy);
    setSearchParams(p, { replace: true });
  }, [searchTerm, selectedCategory, selectedSoftware, minMembers, sortBy, setSearchParams]);

  const filteredForums = forums
    .filter((forum) => {
      const nameMatch = forum.name.toLowerCase().includes(searchTerm.toLowerCase());
      const categoryMatch = forum.category
        ? forum.category.toLowerCase().includes(searchTerm.toLowerCase())
        : false;
      const categoryFilter = !selectedCategory || forum.category === selectedCategory;
      const softwareFilter = !selectedSoftware || forum.software === selectedSoftware;
      const membersFilter = minMembers <= 0 || forum.members_count >= minMembers;
      return (
        (nameMatch || categoryMatch) &&
        categoryFilter &&
        softwareFilter &&
        membersFilter
      );
    })
    .sort((a, b) => {
      if (sortBy === 'score') return calculateScore(b) - calculateScore(a);
      if (sortBy === 'growth') {
        const ga = getForumGrowthPercent(a, 'members') ?? -Infinity;
        const gb = getForumGrowthPercent(b, 'members') ?? -Infinity;
        return gb - ga;
      }
      if (sortBy === 'founded_year') return (a.founded_year || 0) - (b.founded_year || 0);
      return ((b[sortBy as keyof Forum] as number) ?? 0) - ((a[sortBy as keyof Forum] as number) ?? 0);
    });

  const sponsoredForums = filteredForums.filter((f) => isSponsorActive(f));
  const nonSponsoredForums = filteredForums.filter((f) => !isSponsorActive(f));

  const finalForums = [...nonSponsoredForums];
  sponsoredForums.forEach(forum => {
    const maxPosition = Math.min(10, finalForums.length);
    const position = getRandomPosition(2, maxPosition);
    finalForums.splice(position - 1, 0, forum);
  });


  const categoryStats = categories.reduce((acc, category) => {
    const categoryForums = forums.filter(forum => forum.category === category.name);
    const previousMonthForums = category.previous_month_forums_count || 0;
    
    acc[category.name] = {
      totalMembers: categoryForums.reduce((sum, forum) => sum + (forum.members_count ?? 0), 0),
      previousTotalMembers: categoryForums.reduce((sum, forum) => sum + (forum.previous_month_members_count ?? 0), 0),
      totalPosts: categoryForums.reduce((sum, forum) => sum + (forum.posts_count ?? 0), 0),
      previousTotalPosts: categoryForums.reduce((sum, forum) => sum + (forum.previous_month_posts_count ?? 0), 0),
      forumCount: categoryForums.length,
      previousForumCount: previousMonthForums,
      totalTopics: categoryForums.reduce((sum, forum) => sum + (forum.topics_count ?? 0), 0),
      previousTotalTopics: categoryForums.reduce((sum, forum) => sum + (forum.previous_month_topics_count ?? 0), 0),
      avgScore: Math.round(categoryForums.reduce((sum, forum) => sum + calculateScore(forum), 0) / (categoryForums.length || 1))
    };
    return acc;
  }, {} as Record<string, {
    totalMembers: number;
    previousTotalMembers: number;
    totalPosts: number;
    previousTotalPosts: number;
    forumCount: number;
    previousForumCount: number;
    totalTopics: number;
    previousTotalTopics: number;
    avgScore: number;
  }>);

  const formatNumber = (num: number | null): string => {
    if (!num) return '0';
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const retryDataFetch = () => {
    const initializeData = async () => {
      setLoading(true);
      setError(null);
      try {
        await Promise.all([
          fetchSettings(),
          fetchForums(),
          fetchCategories()
        ]);
      } catch (error) {
        console.error('Error initializing data:', error);
        setError('Veriler yüklenirken bir hata oluştu. Lütfen internet bağlantınızı kontrol edin.');
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-blue-50 to-indigo-50'} transition-colors duration-200`}>
        <div className="container mx-auto px-2 sm:px-4 py-8">
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${darkMode ? 'border-indigo-400' : 'border-indigo-600'} mb-4`}></div>
            <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Forum verileri yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-blue-50 to-indigo-50'} transition-colors duration-200`}>
        <div className="container mx-auto px-2 sm:px-4 py-8">
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-8 shadow-sm max-w-md w-full text-center`}>
              <AlertCircle className={`mx-auto mb-4 ${darkMode ? 'text-red-400' : 'text-red-500'}`} size={48} />
              <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`}>
                Bağlantı Hatası
              </h2>
              <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
                {error}
              </p>
              <button
                onClick={retryDataFetch}
                className={`px-6 py-3 ${darkMode ? 'bg-indigo-500 hover:bg-indigo-600' : 'bg-indigo-600 hover:bg-indigo-700'} text-white rounded-lg transition-colors flex items-center gap-2 mx-auto`}
              >
                <RefreshCw size={18} />
                Tekrar Dene
              </button>
              <div className={`mt-6 p-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg text-sm`}>
                <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-2`}>
                  <strong>Olası çözümler:</strong>
                </p>
                <ul className={`${darkMode ? 'text-gray-400' : 'text-gray-500'} text-left space-y-1`}>
                  <li>• İnternet bağlantınızı kontrol edin</li>
                  <li>• Sayfayı yenileyin (F5)</li>
                  <li>• Birkaç dakika sonra tekrar deneyin</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen overflow-x-hidden ${darkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-blue-50 to-indigo-50'} transition-colors duration-200`}>
      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8 max-w-7xl">
        <header className="text-center mb-8 sm:mb-12">
          <h1 className={`text-2xl sm:text-3xl lg:text-4xl font-bold ${darkMode ? 'text-white' : 'text-indigo-900'} mb-4`}>
            {settings.site_name}
          </h1>
          <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} max-w-2xl mx-auto`}>
            {settings.description}
          </p>
        </header>

        <div className="mb-8 flex flex-col lg:flex-row gap-4 items-stretch lg:items-center">
          <div className="relative flex-1 min-w-0 w-full lg:max-w-xl">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} size={20} />
            <input
              type="search"
              placeholder="Forum veya kategori ara..."
              className={`w-full min-h-[44px] pl-10 pr-4 py-3 rounded-lg border text-base ${
                darkMode
                  ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-400 focus:ring-indigo-500'
                  : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-500 focus:ring-indigo-500'
              } focus:outline-none focus:ring-2 transition-colors`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex flex-wrap gap-2 items-center w-full lg:w-auto lg:flex-shrink-0">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              aria-label="Kategori filtresi"
              className={`min-h-[44px] px-4 py-3 rounded-lg border text-base ${
                darkMode
                  ? 'bg-gray-800 border-gray-700 text-white'
                  : 'bg-white border-gray-200 text-gray-900'
              } focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors w-full lg:w-auto`}
            >
              <option value="">Tüm Kategoriler</option>
              {categories.map(category => (
                <option key={category.id} value={category.name}>{category.name}</option>
              ))}
            </select>

            <select
              value={selectedSoftware}
              onChange={(e) => setSelectedSoftware(e.target.value)}
              aria-label="Yazılım filtresi"
              className={`min-h-[44px] px-4 py-3 rounded-lg border text-base ${
                darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
              } focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full lg:w-auto`}
            >
              <option value="">Tüm Yazılımlar</option>
              {softwareList.map((sw) => (
                <option key={sw} value={sw}>{sw}</option>
              ))}
            </select>

            <input
              type="number"
              min={0}
              placeholder="Min. üye"
              value={minMembers || ''}
              onChange={(e) => setMinMembers(Number(e.target.value) || 0)}
              aria-label="Minimum üye sayısı"
              className={`w-full sm:w-32 min-h-[44px] px-3 py-3 rounded-lg border text-base ${
                darkMode
                  ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-400'
                  : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-500'
              }`}
            />

            <div className="flex flex-wrap gap-2 w-full lg:w-auto">
              <button
                className={`min-h-[44px] px-4 py-3 rounded-lg flex items-center gap-2 text-sm ${
                  sortBy === 'score'
                    ? darkMode ? 'bg-indigo-500 text-white' : 'bg-indigo-600 text-white'
                    : darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-50'
                } transition-colors flex-1 lg:flex-none justify-center`}
                onClick={() => setSortBy('score')}
              >
                <Trophy size={18} />
                Skor
              </button>
              <button
                className={`min-h-[44px] px-4 py-3 rounded-lg flex items-center gap-2 text-sm ${
                  sortBy === 'topics_count'
                    ? darkMode ? 'bg-indigo-500 text-white' : 'bg-indigo-600 text-white'
                    : darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-50'
                } transition-colors flex-1 lg:flex-none justify-center`}
                onClick={() => setSortBy('topics_count')}
              >
                <MessageCircle size={18} />
                Konu
              </button>
              <button
                className={`min-h-[44px] px-4 py-3 rounded-lg flex items-center gap-2 text-sm ${
                  sortBy === 'posts_count'
                    ? darkMode ? 'bg-indigo-500 text-white' : 'bg-indigo-600 text-white'
                    : darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-50'
                } transition-colors flex-1 lg:flex-none justify-center`}
                onClick={() => setSortBy('posts_count')}
              >
                <Activity size={18} />
                İleti
              </button>
              <button
                className={`min-h-[44px] px-4 py-3 rounded-lg flex items-center gap-2 text-sm ${
                  sortBy === 'members_count'
                    ? darkMode ? 'bg-indigo-500 text-white' : 'bg-indigo-600 text-white'
                    : darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-50'
                } transition-colors flex-1 lg:flex-none justify-center`}
                onClick={() => setSortBy('members_count')}
              >
                <Users size={18} />
                Üye
              </button>
              <button
                className={`min-h-[44px] px-4 py-3 rounded-lg flex items-center gap-2 text-sm ${
                  sortBy === 'growth'
                    ? darkMode ? 'bg-indigo-500 text-white' : 'bg-indigo-600 text-white'
                    : darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-50'
                } transition-colors flex-1 lg:flex-none justify-center`}
                onClick={() => setSortBy('growth')}
              >
                <TrendingUp size={18} />
                Büyüme
              </button>
              <button
                className={`min-h-[44px] px-4 py-3 rounded-lg flex items-center gap-2 text-sm ${
                  sortBy === 'founded_year'
                    ? darkMode ? 'bg-indigo-500 text-white' : 'bg-indigo-600 text-white'
                    : darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-50'
                } transition-colors flex-1 lg:flex-none justify-center`}
                onClick={() => setSortBy('founded_year')}
              >
                <Calendar size={18} />
                Yıl
              </button>
            </div>
          </div>
        </div>

        <div className="grid gap-6">
          {finalForums.map((forum, index) => {
            const scoreDelta = getScoreChange(forum);
            const currentRank = index + 1;
            const globalRank = globalScoreRanks.get(forum.id) ?? '—';
            const listRankDiffers = currentRank !== globalRank;
            const previousGlobalRank = globalPreviousScoreRanks.get(forum.id);
            const rankDelta =
              typeof globalRank === 'number' && typeof previousGlobalRank === 'number'
                ? previousGlobalRank - globalRank
                : null;

            return (
              <ForumCardWrapper
                key={forum.name}
                to={`/forum/${forum.forum_id}`}
                forumId={forum.forum_id}
                darkMode={darkMode}
                adminMeta={forum as ForumBase}
                className={`flex flex-col ${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'} rounded-xl p-4 sm:p-6 shadow-sm transition-all duration-200 cursor-pointer relative`}
              >
                {isSponsorActive(forum) && (
                  <div
                    className={`absolute top-3 right-3 sm:top-4 sm:right-4 flex items-center gap-1 px-2 py-1 rounded-full text-xs shrink-0 z-10 ${
                      darkMode ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-600'
                    }`}
                  >
                    <Star size={14} className="fill-current" />
                    <span>Sponsorlu</span>
                  </div>
                )}
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 flex-1 min-h-0">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                      <div className="flex shrink-0 flex-col gap-0.5">
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-2xl font-bold leading-none tabular-nums ${
                              darkMode ? 'text-indigo-400' : 'text-indigo-600'
                            }`}
                            title={`Forum puanına göre genel sıra: ${globalRank}`}
                          >
                            #{globalRank}
                          </span>
                          {rankDelta !== null && (
                            <span
                              className={`inline-flex items-center gap-1 text-xs font-medium tabular-nums min-w-[2rem] ${
                                rankDelta > 0
                                  ? 'text-green-500'
                                  : rankDelta < 0
                                    ? 'text-red-500'
                                    : 'text-blue-500'
                              }`}
                              title="Geçen aya göre genel sıra değişimi"
                            >
                              {rankDelta > 0 ? (
                                <>
                                  <ArrowUpRight size={14} />
                                  {rankDelta}
                                </>
                              ) : rankDelta < 0 ? (
                                <>
                                  <ArrowDownRight size={14} />
                                  {Math.abs(rankDelta)}
                                </>
                              ) : (
                                <>
                                  <Minus size={14} />
                                  -
                                </>
                              )}
                            </span>
                          )}
                        </div>
                        {listRankDiffers && (
                          <span
                            className={`text-[11px] tabular-nums ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                            title="Seçili sıralama veya filtreyle bu listedeki konum"
                          >
                            Bu listede #{currentRank}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        {forum.logo_url && (
                          <img
                            src={forum.logo_url}
                            alt=""
                            className="max-w-[120px] sm:max-w-[150px] max-h-[45px] w-auto h-auto object-contain shrink-0"
                          />
                        )}
                        <div>
                          <h2 className={`text-lg sm:text-xl font-semibold flex flex-wrap items-center gap-2 break-words ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {forum.name}
                            <ForumRiskBadge forum={forum as ForumBase} />
                          </h2>
                          {/* Desktop görünüm */}
                          <div className="hidden sm:flex flex-wrap items-center gap-2 text-sm text-gray-500">
                            <Link 
                              to={`/category/${createCategorySlug(forum.category)}`}
                              className={darkMode ? 'text-gray-400 hover:text-indigo-400' : 'hover:text-indigo-600'}
                            >
                              {forum.category}
                            </Link>
                            <span className={darkMode ? 'text-gray-600' : ''}>•</span>
                            <div className="flex items-center gap-1">
                              <Calendar size={14} className={darkMode ? 'text-gray-400' : ''} />
                              <span className={darkMode ? 'text-gray-400' : ''}>{forum.founded_year}</span>
                            </div>
                            {forum.software && (
                              <>
                                <span className={darkMode ? 'text-gray-600' : ''}>•</span>
                                <Link 
                                  to={`/software/${createSoftwareSlug(forum.software)}`}
                                  className={darkMode ? 'text-gray-400 hover:text-indigo-400' : 'hover:text-indigo-600'}
                                >
                                  {forum.software}
                                </Link>
                              </>
                            )}
                            <span className={darkMode ? 'text-gray-600' : ''}>•</span>
                            <div className="flex items-center gap-1">
                              <Clock size={14} className={darkMode ? 'text-gray-400' : ''} />
                              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} whitespace-nowrap`}>
                                {formatLastUpdated(lastUpdatedSource(forum))}
                              </span>
                            </div>
                          </div>
                          
                          {/* Mobil görünüm */}
                          <div className="sm:hidden space-y-2 mt-2">
                            <div className="flex flex-wrap gap-3">
                              <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs ${
                                darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                              }`}>
                                <Link to={`/category/${createCategorySlug(forum.category)}`} className="hover:underline">
                                  {forum.category}
                                </Link>
                              </div>
                              <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs ${
                                darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                              }`}>
                                <Calendar size={12} />
                                <span>{forum.founded_year}</span>
                              </div>
                              {forum.software && (
                                <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs ${
                                  darkMode ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-50 text-indigo-600'
                                }`}>
                                  <Code2 size={12} />
                                  <Link to={`/software/${createSoftwareSlug(forum.software)}`} className="hover:underline">
                                    {forum.software}
                                  </Link>
                                </div>
                              )}
                            </div>
                            <div className={`flex items-center gap-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <Clock size={12} />
                              <span>Son güncelleme: {formatLastUpdated(lastUpdatedSource(forum))}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="flex items-center gap-3">
                        <Trophy className="text-amber-500 shrink-0" size={20} />
                        <div>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Forum Skoru</p>
                          <div className="flex items-center gap-2">
                            <span className={`font-semibold ${darkMode ? 'text-white' : ''}`}>{calculateScore(forum).toLocaleString()}</span>
                            {scoreDelta !== null && (
                              <div
                                className={`flex items-center gap-1 text-sm ${
                                  scoreDelta > 0 ? 'text-green-500' : 'text-red-500'
                                }`}
                              >
                                {scoreDelta > 0 ? (
                                  <ArrowUpRight size={14} />
                                ) : (
                                  <ArrowDownRight size={14} />
                                )}
                                <span>
                                  ({scoreDelta > 0 ? '+' : ''}
                                  {scoreDelta.toLocaleString('tr-TR')})
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <MessageCircle className="text-purple-500 shrink-0" size={20} />
                        <div>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Konu Sayısı</p>
                          <div className="flex items-center gap-2">
                            <span className={`font-semibold ${darkMode ? 'text-white' : ''}`}>{forum.topics_count.toLocaleString()}</span>
                            {formatStatDelta(forum.topics_count, forum.previous_month_topics_count) && (
                              <span className={`text-sm ${
                                (statDelta(forum.topics_count, forum.previous_month_topics_count) ?? 0) > 0
                                  ? 'text-green-500'
                                  : 'text-red-500'
                              }`}>
                                {formatStatDelta(forum.topics_count, forum.previous_month_topics_count)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Activity className="text-green-500 shrink-0" size={20} />
                        <div>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Toplam İleti</p>
                          <div className="flex items-center gap-2">
                            <span className={`font-semibold ${darkMode ? 'text-white' : ''}`}>{forum.posts_count.toLocaleString()}</span>
                            {formatStatDelta(forum.posts_count, forum.previous_month_posts_count) && (
                              <span className={`text-sm ${
                                (statDelta(forum.posts_count, forum.previous_month_posts_count) ?? 0) > 0
                                  ? 'text-green-500'
                                  : 'text-red-500'
                              }`}>
                                {formatStatDelta(forum.posts_count, forum.previous_month_posts_count)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Users className="text-blue-500 shrink-0" size={20} />
                        <div>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Üye Sayısı</p>
                          <div className="flex items-center gap-2">
                            <span className={`font-semibold ${darkMode ? 'text-white' : ''}`}>{forum.members_count.toLocaleString()}</span>
                            {formatStatDelta(forum.members_count, forum.previous_month_members_count) && (
                              <span className={`text-sm ${
                                (statDelta(forum.members_count, forum.previous_month_members_count) ?? 0) > 0
                                  ? 'text-green-500'
                                  : 'text-red-500'
                              }`}>
                                {formatStatDelta(forum.members_count, forum.previous_month_members_count)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div
                    className={`text-xs shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-500'} flex flex-row md:flex-col gap-3 md:gap-1 md:items-end justify-end`}
                  >
                    <div className="flex items-center gap-1 whitespace-nowrap">
                      <MessageCircle size={12} className="shrink-0" />
                      <span>Konular: #{getRankingByField(forum, 'topics_count')}</span>
                    </div>
                    <div className="flex items-center gap-1 whitespace-nowrap">
                      <Activity size={12} className="shrink-0" />
                      <span>İletiler: #{getRankingByField(forum, 'posts_count')}</span>
                    </div>
                    <div className="flex items-center gap-1 whitespace-nowrap">
                      <Users size={12} className="shrink-0" />
                      <span>Üyeler: #{getRankingByField(forum, 'members_count')}</span>
                    </div>
                  </div>
                </div>
              </ForumCardWrapper>
            );
          })}
        </div>
      </div>
      <BackToTop darkMode={darkMode} />
    </div>
  );
};

export default ForumStats;